import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Fade, Typography } from '../library';

/**
 * BuildOverlay
 *
 * An animated, centered overlay that cycles through an array of strings at a
 * fixed interval while showing a construction-like progress indicator made of
 * small squares that fill during each interval and reset on each cycle.
 *
 * Props:
 * - items: string[]                      -> Array of texts to display (required)
 * - intervalMs?: number                  -> Time each text is shown (ms). Default 2000
 * - active?: boolean                     -> Controls visibility/animation. Default true
 * - squares?: number                     -> Number of progress squares. Default 10
 * - opacity?: number                     -> Overlay text opacity (0..1). Default 0.85
 * - color?: string                       -> Text color. Default theme.palette.common.white
 * - sx?: object                          -> Style overrides for the wrapper Box
 * - onIndexChange?: (index: number) => void -> Callback when item index changes
 */
const BuildOverlay = props => {
    const {
        items = [],
        intervalMs = 2000,
        active = false,
        squares = 10,
        opacity = 0.85,
        color = 'white',
        sx = {},
        onIndexChange
    } = props;

    const [index, setIndex] = useState(0);
    const [now, setNow] = useState(Date.now());
    const startRef = useRef(Date.now());

    // refs to manage animation frame and timeout without causing rerenders
    const rafIdRef = useRef(null);
    const timeoutIdRef = useRef(null);

    // rAF loop to update progress smoothly without blocking UI
    useEffect(() => {
        if (!active || items.length === 0) return;

        const loop = () => {
            // rAF is synced with browser paint, smoother and paused in background tabs
            setNow(Date.now());
            rafIdRef.current = window.requestAnimationFrame(loop);
        };

        rafIdRef.current = window.requestAnimationFrame(loop);
        return () => {
            if (rafIdRef.current) window.cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        };
    }, [active, items.length]);

    // advance index when interval elapses using chained setTimeout to avoid interval drift
    useEffect(() => {
        if (!active || items.length === 0) return;

        const scheduleNext = () => {
            const elapsed = Date.now() - startRef.current;
            const remaining = Math.max(0, intervalMs - elapsed);
            timeoutIdRef.current = window.setTimeout(() => {
                setIndex(prev => {
                    const next =
                        items.length > 0 ? (prev + 1) % items.length : 0;
                    if (typeof onIndexChange === 'function')
                        onIndexChange(next);
                    return next;
                });
                startRef.current = Date.now();
                scheduleNext();
            }, remaining);
        };

        // initialize cycle start and schedule
        startRef.current = Date.now();
        scheduleNext();

        return () => {
            if (timeoutIdRef.current) window.clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        };
    }, [active, items.length, intervalMs, onIndexChange]);

    // clamp index if items array changes size
    useEffect(() => {
        if (items.length === 0) return;
        setIndex(prev => (prev >= items.length ? 0 : prev));
    }, [items.length]);

    // reset timers when activated
    useEffect(() => {
        if (active) {
            startRef.current = Date.now();
            setNow(Date.now());
        }
    }, [active]);

    const progressCount = useMemo(() => {
        if (!active || items.length === 0) return 0;
        const elapsed = Date.now() - startRef.current;
        const frac = Math.max(0, Math.min(1, elapsed / intervalMs));
        return Math.floor(frac * squares);
    }, [now, active, items.length, intervalMs, squares]);

    if (!active || items.length === 0) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 9999,
                ...sx
            }}
        >
            <Fade in={true} timeout={500}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.2)'
                    }}
                >
                    <Typography
                        variant='h3'
                        sx={{
                            color,
                            opacity,
                            textAlign: 'center',
                            textShadow: '0 2px 8px rgba(0,0,0,0.35)'
                        }}
                    >
                        {items[index]}
                    </Typography>

                    <Box sx={{ height: 12 }} />

                    <Box
                        sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}
                    >
                        {new Array(squares).fill(0).map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 0.5,
                                    bgcolor:
                                        i < progressCount
                                            ? 'primary.main'
                                            : 'rgba(255,255,255,0.25)',
                                    boxShadow:
                                        i < progressCount
                                            ? '0 0 6px rgba(25,118,210,0.6)'
                                            : 'none',
                                    transition:
                                        'background-color 120ms linear, box-shadow 120ms linear'
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Fade>
        </Box>
    );
};

export default BuildOverlay;
